import type { NeckDiagramSheet } from "@wmf/shared/neckDiagrams";
import { get, send } from "../api.js";
export interface SavedNeckDiagram {
  id: string;
  title: string;
  document: NeckDiagramSheet;
  version: number;
  originNeckDiagramId?: string | null;
  updatedAt?: string;
}
export const lessonNeckDiagrams = (lessonId: string) =>
  get<{ diagrams: SavedNeckDiagram[]; canManage: boolean }>(
    `/neck-diagrams/lessons/${encodeURIComponent(lessonId)}`,
  );
export const readNeckDiagram = (id: string) =>
  get<{ diagram: SavedNeckDiagram; canEdit: boolean; canCopy: boolean; canShare: boolean; canPublish: boolean }>(`/neck-diagrams/${encodeURIComponent(id)}`);
export const saveNeckDiagram = (
  document: NeckDiagramSheet,
  previous?: SavedNeckDiagram | null,
) =>
  send<{ diagram: SavedNeckDiagram }>(
    previous ? "PUT" : "POST",
    `/neck-diagrams${previous ? `/${previous.id}` : ""}`,
    {
      title: document.title,
      document,
      ...(previous ? { version: previous.version } : {}),
    },
  );
export const attachNeckDiagram = (lessonId: string, neckDiagramId: string) =>
  send("POST", `/neck-diagrams/lessons/${lessonId}`, { neckDiagramId });
export const detachNeckDiagram = (lessonId: string, neckDiagramId: string) =>
  send("DELETE", `/neck-diagrams/lessons/${lessonId}/${neckDiagramId}`);

export const neckDiagramCapabilities = () => get<{ canCreate: boolean; canPlay: boolean; canUseLibrary: boolean; instrument: import("@wmf/shared/neckDiagrams").InstrumentId | null }>("/neck-diagrams/capabilities");
export const listNeckDiagrams = (search = "", offset = 0) => get<{ diagrams: SavedNeckDiagram[]; nextOffset: number | null; canCreate: boolean; canPlay: boolean; canUseLibrary: boolean; instrument: import("@wmf/shared/neckDiagrams").InstrumentId | null }>("/neck-diagrams", { search, offset: String(offset) });
export const deleteNeckDiagram = (id: string) => send("DELETE", `/neck-diagrams/${encodeURIComponent(id)}`);
export const copyNeckDiagram = (id: string, shareId?: string) => send<{ diagram: SavedNeckDiagram }>("POST", `/neck-diagrams/${encodeURIComponent(id)}/copy`, shareId ? { shareId } : {});
export interface SharedNeckDiagram {
  shareId: string; id: string; title: string; document: NeckDiagramSheet; note: string | null;
  sharedAt: string; practiceTodoId: string | null; practiceDone: boolean | null; withdrawnAt: string | null;
}
export const neckDiagramsForStudent = (studentId: string) => get<{ shared: SharedNeckDiagram[]; authored: SavedNeckDiagram[] }>(`/neck-diagrams/for-student/${encodeURIComponent(studentId)}`);
export const readNeckDiagramShare = (shareId: string) => get<{ sheet: { id: string; title: string; document: NeckDiagramSheet }; shareId: string; studentId: string; note: string | null; canCopy: boolean; canWithdraw: boolean }>(`/neck-diagrams/shares/${encodeURIComponent(shareId)}`);
export const neckDiagramShareHref = (shareId: string) => `/neck-diagrams/shared/${encodeURIComponent(shareId)}`;
export interface NeckDiagramShare {
  id: string; studentId: string; studentName: string; note: string | null;
  sharedAt: string; revokedAt: string | null; canWithdraw: boolean;
}
export const listNeckDiagramShares = (id: string) => get<{ shares: NeckDiagramShare[] }>(`/neck-diagrams/${encodeURIComponent(id)}/shares`);
export const shareableNeckDiagramStudents = (search: string) => get<{ students: { id: string; name: string }[] }>("/neck-diagrams/shareable-students", { search });
export const shareNeckDiagram = (id: string, studentId: string, note: string, setAsPractice: boolean) => send<{ alreadyShared: boolean; practiceTodoId: string | null }>("POST", `/neck-diagrams/${encodeURIComponent(id)}/shares`, { studentId, note, setAsPractice });
export const withdrawNeckDiagramShare = (id: string, shareId: string) => send("DELETE", `/neck-diagrams/${encodeURIComponent(id)}/shares/${encodeURIComponent(shareId)}`);

export interface NeckDiagramPreference {
  handedness: "left" | "right" | null;
  previousHandedness: string | null;
  canSet: boolean;
}
export const neckDiagramPreference = (studentId: string) => get<NeckDiagramPreference>(`/neck-diagrams/students/${encodeURIComponent(studentId)}/handedness`);
export const saveNeckDiagramPreference = (studentId: string, handedness: NeckDiagramPreference["handedness"], previousHandedness: string | null) => send<NeckDiagramPreference>("PUT", `/neck-diagrams/students/${encodeURIComponent(studentId)}/handedness`, { handedness, previousHandedness });
export type NeckDiagramCategory = "chord" | "scale" | "arpeggio" | "other";
export interface NeckDiagramLibraryEntry {
  id: string; neckDiagramId: string; snapshotTitle: string; snapshotDocument: NeckDiagramSheet;
  instrument: import("@wmf/shared/neckDiagrams").InstrumentId; category: NeckDiagramCategory; canWithdraw: boolean;
}
export const listNeckDiagramStaffLibrary = (instrument: string, category: string, offset = 0) => get<{ entries: NeckDiagramLibraryEntry[]; nextOffset: number | null }>("/neck-diagrams/library", { ...(instrument ? { instrument } : {}), ...(category ? { category } : {}), offset: String(offset) });
export const publishNeckDiagram = (id: string, category: NeckDiagramCategory, instrument: NeckDiagramLibraryEntry["instrument"]) => send<{ alreadyPublished: boolean }>("POST", `/neck-diagrams/${encodeURIComponent(id)}/library`, { category, instrument });
export const withdrawNeckDiagramLibraryEntry = (id: string) => send("DELETE", `/neck-diagrams/library/${encodeURIComponent(id)}`);
export const copyNeckDiagramLibraryEntry = (id: string) => send<{ diagram: SavedNeckDiagram }>("POST", `/neck-diagrams/library/${encodeURIComponent(id)}/copy`, {});
